import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { toast } from 'react-hot-toast'

interface HistoryEvent {
  timestamp: string
  action: string
  status: string
  description: string
  user: string
  metadata: Record<string, any>
}

interface Patient {
  patient_id: string
  full_name: string
  date_of_birth: string
  gender: string
}

const PatientHistory: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const [patient, setPatient] = useState<Patient | null>(null)
  const [history, setHistory] = useState<HistoryEvent[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [filter, setFilter] = useState<'all' | 'upload' | 'update' | 'create'>('all')

  useEffect(() => {
    if (id) {
      fetchPatientHistory()
    }
  }, [id])

  const fetchPatientHistory = async () => {
    try {
      setIsLoading(true)
      const response = await fetch(`/api/v1/pathology/patients/${id}/history/`)
      if (response.ok) {
        const data = await response.json()
        setPatient({
          patient_id: data.patient_id,
          full_name: data.patient_name,
          date_of_birth: '',
          gender: ''
        })
        setHistory(data.history || [])
      } else {
        toast.error('Không thể tải lịch sử bệnh nhân')
      }
    } catch (error) {
      console.error('Error fetching patient history:', error)
      toast.error('Lỗi khi tải lịch sử')
    } finally {
      setIsLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  }

  const getActionIcon = (action: string) => {
    const iconMap: Record<string, string> = {
      create: '➕',
      update: '✏️',
      delete: '🗑️',
      upload: '📤',
      view: '👁️',
      export: '📄',
      login: '🔐',
      logout: '🚪'
    }
    return iconMap[action] || '📋'
  }

  const getStatusColor = (status: string) => {
    const colorMap: Record<string, string> = {
      success: 'bg-green-100 text-green-800 border-green-300',
      failed: 'bg-red-100 text-red-800 border-red-300',
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300'
    }
    return colorMap[status] || 'bg-gray-100 text-gray-800 border-gray-300'
  }

  const filteredHistory = history.filter(event => {
    if (filter === 'all') return true
    return event.action === filter
  })

  const actionCounts = {
    all: history.length,
    upload: history.filter(e => e.action === 'upload').length,
    update: history.filter(e => e.action === 'update').length,
    create: history.filter(e => e.action === 'create').length
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              📜 Lịch Sử Workflow
            </h1>
            {patient && (
              <p className="text-gray-600 mt-1">
                Bệnh nhân: <span className="font-semibold">{patient.full_name}</span> ({patient.patient_id})
              </p>
            )}
          </div>
          <Link
            to={`/pathology/patients/${id}`}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            ← Quay lại
          </Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="text-3xl font-bold text-blue-700">{actionCounts.all}</div>
          <div className="text-sm text-blue-600 mt-1">📊 Tổng sự kiện</div>
        </div>

        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <div className="text-3xl font-bold text-green-700">{actionCounts.upload}</div>
          <div className="text-sm text-green-600 mt-1">📤 Upload ảnh</div>
        </div>

        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
          <div className="text-3xl font-bold text-yellow-700">{actionCounts.update}</div>
          <div className="text-sm text-yellow-600 mt-1">✏️ Cập nhật</div>
        </div>

        <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
          <div className="text-3xl font-bold text-purple-700">{actionCounts.create}</div>
          <div className="text-sm text-purple-600 mt-1">➕ Tạo mới</div>
        </div>
      </div>

      {/* Filter Buttons */}
      <div className="mb-4 flex space-x-2">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-md font-medium ${
            filter === 'all'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          📋 Tất cả ({actionCounts.all})
        </button>
        <button
          onClick={() => setFilter('upload')}
          className={`px-4 py-2 rounded-md font-medium ${
            filter === 'upload'
              ? 'bg-green-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          📤 Upload ({actionCounts.upload})
        </button>
        <button
          onClick={() => setFilter('update')}
          className={`px-4 py-2 rounded-md font-medium ${
            filter === 'update'
              ? 'bg-yellow-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          ✏️ Cập nhật ({actionCounts.update})
        </button>
        <button
          onClick={() => setFilter('create')}
          className={`px-4 py-2 rounded-md font-medium ${
            filter === 'create'
              ? 'bg-purple-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          ➕ Tạo mới ({actionCounts.create})
        </button>
      </div>

      {/* Timeline */}
      <div className="bg-white rounded-lg shadow">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
            <p className="mt-2">Đang tải lịch sử...</p>
          </div>
        ) : filteredHistory.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p className="text-lg">Không có sự kiện nào</p>
          </div>
        ) : (
          <div className="p-6">
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-200"></div>

              {/* Timeline events */}
              <div className="space-y-6">
                {filteredHistory.map((event, index) => (
                  <div key={index} className="relative pl-16">
                    {/* Icon */}
                    <div className={`absolute left-0 w-16 h-16 rounded-full flex items-center justify-center text-2xl border-4 border-white shadow-md ${getStatusColor(event.status)}`}>
                      {getActionIcon(event.action)}
                    </div>

                    {/* Content */}
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(event.status)}`}>
                              {event.status.toUpperCase()}
                            </span>
                            <span className="text-xs text-gray-500">
                              {formatDate(event.timestamp)}
                            </span>
                          </div>
                          <h3 className="text-sm font-semibold text-gray-900 mt-2">
                            {event.description}
                          </h3>
                        </div>
                      </div>

                      <div className="text-xs text-gray-600 mb-2">
                        👤 <span className="font-medium">{event.user}</span>
                      </div>

                      {/* Metadata */}
                      {Object.keys(event.metadata).length > 0 && (
                        <details className="mt-3">
                          <summary className="text-xs text-blue-600 cursor-pointer hover:text-blue-800">
                            📋 Chi tiết
                          </summary>
                          <div className="mt-2 bg-white rounded p-2 border border-gray-200">
                            <pre className="text-xs text-gray-700 overflow-x-auto">
                              {JSON.stringify(event.metadata, null, 2)}
                            </pre>
                          </div>
                        </details>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default PatientHistory
